import React, { useState, useEffect, useRef } from 'react';
import Table from './Table';
import './FloorPlan.css';

function FloorPlan({
  tables,
  onTableUpdate,
  onDevMode,
  onEmbedTables,
  onUnembedGroup,
  onResetCurrentService
}) {
  const [isEmbedMode, setIsEmbedMode] = useState(false);
  const [embedSourceId, setEmbedSourceId] = useState(null);
  const [embedNotice, setEmbedNotice] = useState('');
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Oui',
    cancelLabel: 'Non',
    requirePassword: false,
    password: '',
    passwordError: ''
  });
  const dialogResolverRef = useRef(null);
  const [stats, setStats] = useState({
    totalCapacity: 60,
    occupiedSeats: 0,
    freeSeats: 60,
    occupiedTables: 0,
    freeTables: 23
  });

  useEffect(() => {
    const totalCapacity = tables.reduce((sum, t) => sum + t.capacity, 0);
    const occupiedSeats = tables.reduce((sum, t) => sum + t.guests, 0);
    const freeSeats = totalCapacity - occupiedSeats;
    const occupiedTables = tables.filter(t => t.guests > 0).length;

    setStats({
      totalCapacity,
      occupiedSeats,
      freeSeats,
      occupiedTables,
      freeTables: tables.length - occupiedTables
    });
  }, [tables]);

  useEffect(() => {
    if (!isEmbedMode) {
      setEmbedSourceId(null);
      return;
    }

    if (embedSourceId && !tables.find((t) => t.id === embedSourceId)) {
      setEmbedSourceId(null);
    }
  }, [isEmbedMode, embedSourceId, tables]);

  useEffect(() => {
    if (!embedNotice) {
      return undefined;
    }

    const timer = setTimeout(() => setEmbedNotice(''), 2500);
    return () => clearTimeout(timer);
  }, [embedNotice]);

  const openSiteDialog = ({ title, message, confirmLabel = 'Oui', cancelLabel = 'Non', requirePassword = false }) => (
    new Promise((resolve) => {
      dialogResolverRef.current = resolve;
      setDialogState({
        open: true,
        title,
        message,
        confirmLabel,
        cancelLabel,
        requirePassword,
        password: '',
        passwordError: ''
      });
    })
  );

  const closeSiteDialog = (result) => {
    if (dialogResolverRef.current) {
      dialogResolverRef.current(result);
      dialogResolverRef.current = null;
    }
    setDialogState((prev) => ({ ...prev, open: false, password: '', passwordError: '' }));
  };

  const confirmSiteDialog = () => {
    if (dialogState.requirePassword && dialogState.password !== '1515') {
      setDialogState((prev) => ({ ...prev, passwordError: 'Mot de passe invalide.' }));
      return;
    }
    closeSiteDialog(true);
  };

  const buildVisualTables = () => {
    if (isEmbedMode) {
      return tables;
    }

    const grouped = new Map();
    tables.forEach((t) => {
      if (!t.groupId) {
        return;
      }
      if (!grouped.has(t.groupId)) {
        grouped.set(t.groupId, []);
      }
      grouped.get(t.groupId).push(t);
    });

    const mergedGroupIds = new Set();
    const mergedMemberIds = new Set();
    const mergedTables = [];
    grouped.forEach((groupTables, groupId) => {
      if (groupTables.length === 2 && groupTables.every((t) => t.capacity === 2)) {
        const sorted = [...groupTables].sort((a, b) => a.number - b.number);
        const t1 = sorted[0];
        const t2 = sorted[1];
        mergedGroupIds.add(groupId);
        mergedMemberIds.add(t1.id);
        mergedMemberIds.add(t2.id);
        mergedTables.push({
          id: `merged-${groupId}`,
          groupId,
          type: 'rect',
          capacity: 4,
          guests: (t1.guests || 0) + (t2.guests || 0),
          x: Math.round((t1.x + t2.x) / 2),
          y: Math.round((t1.y + t2.y) / 2),
          reservationName: t1.reservationName || t2.reservationName || '',
          reservationTime: t1.reservationTime || t2.reservationTime || '',
          isMerged: true,
          displayNumber: `${t1.number}/${t2.number}`
        });
      }
    });

    const nonMerged = tables.filter((t) => !mergedMemberIds.has(t.id));
    return [...nonMerged, ...mergedTables];
  };

  const getDistance = (t1, t2) => {
    const dx = t1.x - t2.x;
    const dy = t1.y - t2.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleEmbedSelect = async (tableId) => {
    if (!isEmbedMode) {
      return;
    }

    const table = tables.find((t) => t.id === tableId);
    if (!table) {
      return;
    }

    if (!embedSourceId) {
      setEmbedSourceId(tableId);
      setEmbedNotice(`Table #${table.number} sélectionnée`);
      return;
    }

    if (embedSourceId === tableId) {
      setEmbedSourceId(null);
      return;
    }

    const source = tables.find((t) => t.id === embedSourceId);
    if (!source) {
      setEmbedSourceId(null);
      return;
    }

    if (source.groupId && source.groupId === table.groupId) {
      setEmbedNotice('Ces tables sont déjà emboîtées');
      setEmbedSourceId(null);
      return;
    }

    if (getDistance(source, table) > 180) {
      const continueFar = await openSiteDialog({
        title: 'Tables eloignees',
        message: 'Ces tables semblent eloignees. Voulez-vous continuer ?'
      });
      if (!continueFar) {
        return;
      }
    }

    const confirmed = await openSiteDialog({
      title: 'Confirmer emboitage',
      message: `Voulez-vous emboiter la table #${source.number} avec la table #${table.number} ?`
    });
    if (!confirmed) {
      return;
    }

    const ok = await onEmbedTables(source.id, table.id);
    if (ok) {
      setEmbedSourceId(null);
      setEmbedNotice(`Tables #${source.number} et #${table.number} emboîtées`);
    } else {
      setEmbedNotice('Emboîtage impossible pour le moment');
    }
  };

  const handleUnembedSelected = async () => {
    if (!embedSourceId) {
      return;
    }
    const source = tables.find((t) => t.id === embedSourceId);
    if (!source || !source.groupId) {
      return;
    }

    const ok = await onUnembedGroup(source.groupId);
    if (ok) {
      setEmbedSourceId(null);
      setEmbedNotice('Groupe désemboîté');
    } else {
      setEmbedNotice('Désemboîtage impossible pour le moment');
    }
  };

  const handleMergedTableUpdate = async (groupId, guestCount, reservationName, reservationTime) => {
    const groupTables = tables
      .filter((t) => t.groupId === groupId)
      .sort((a, b) => a.number - b.number);

    if (!groupTables.length) {
      return;
    }

    let remaining = Math.max(0, guestCount);
    const updates = groupTables.map((t) => {
      const guestsForTable = Math.min(t.capacity, remaining);
      remaining -= guestsForTable;
      return onTableUpdate(t.id, guestsForTable, reservationName, reservationTime);
    });

    await Promise.all(updates);
  };

  const occupancyPercentage = Math.round((stats.occupiedSeats / stats.totalCapacity) * 100);
  const visualTables = buildVisualTables();

  const handleResetService = async () => {
    const confirmed = await openSiteDialog({
      title: 'Reinitialiser le service',
      message: 'Cette action va supprimer toutes les reservations et remettre le plan a l\'origine. Etes-vous sur ?',
      requirePassword: true
    });

    if (!confirmed) {
      return;
    }

    const ok = await onResetCurrentService();
    if (ok) {
      setEmbedNotice('Service reinitialise avec succes');
      setIsEmbedMode(false);
      setEmbedSourceId(null);
    } else {
      setEmbedNotice('Reinitialisation impossible pour le moment');
    }
  };

  return (
    <div className="floor-plan-container">
      <div className="floor-plan-top">
        <div className="stats-bar">
          <div className="stat-item">
            <div className="stat-label">Places Occupées</div>
            <div className="stat-value">{stats.occupiedSeats}/{stats.totalCapacity}</div>
            <div className="stat-subtext">({occupancyPercentage}%)</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Places Disponibles</div>
            <div className="stat-value">{stats.freeSeats}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Tables Occupées</div>
            <div className="stat-value">{stats.occupiedTables}/{tables.length}</div>
          </div>
        </div>

        <div className="actions-panel">
          <button
            className={`btn-embed ${isEmbedMode ? 'active' : ''}`}
            onClick={() => setIsEmbedMode((prev) => !prev)}
          >
            {isEmbedMode ? 'Emboitage: ON' : 'Emboitage: OFF'}
          </button>
          {isEmbedMode && (
            <button
              className="btn-unembed"
              onClick={handleUnembedSelected}
              disabled={!embedSourceId || !tables.find((t) => t.id === embedSourceId)?.groupId}
            >
              Desemboiter
            </button>
          )}
          <button className="btn-reset-service" onClick={handleResetService}>
            Reinitialiser service
          </button>
          <button className="btn-dev" onClick={onDevMode}>
            🔧 Dev
          </button>
        </div>
      </div>

      {isEmbedMode && (
        <div className="embed-banner">
          {embedSourceId
            ? `Table source: #${tables.find((t) => t.id === embedSourceId)?.number}. Cliquez une table proche pour confirmer.`
            : 'Mode emboitage actif: cliquez une premiere table puis une table proche.'}
        </div>
      )}

      {embedNotice && <div className="embed-notice">{embedNotice}</div>}

      <div className="canvas-container">
        <div className="floor-plan-canvas">
          {visualTables.map(table => (
            <Table
              key={table.id}
              table={table}
              onUpdate={onTableUpdate}
              onMergedUpdate={handleMergedTableUpdate}
              embedMode={isEmbedMode}
              onEmbedSelect={handleEmbedSelect}
              isEmbedSource={embedSourceId === table.id}
            />
          ))}
        </div>
      </div>

      <div className="legend">
        <div className="legend-item">
          <div className="legend-box free"></div>
          <span>Table libre</span>
        </div>
        <div className="legend-item">
          <div className="legend-box occupied"></div>
          <span>Table occupée</span>
        </div>
        <div className="legend-item">
          <div className="legend-circle"></div>
          <span>Table ronde</span>
        </div>
        <div className="legend-item">
          <div className="legend-rect"></div>
          <span>Table rectangle</span>
        </div>
      </div>

      {dialogState.open && (
        <div className="site-dialog-overlay" onClick={() => closeSiteDialog(false)}>
          <div className="site-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{dialogState.title}</h3>
            <p>{dialogState.message}</p>

            {dialogState.requirePassword && (
              <div className="site-dialog-password">
                <label>Mot de passe secret</label>
                <input
                  type="password"
                  value={dialogState.password}
                  onChange={(e) => setDialogState((prev) => ({ ...prev, password: e.target.value, passwordError: '' }))}
                  placeholder="Entrez le mot de passe"
                  autoFocus
                />
                {dialogState.passwordError && <span className="site-dialog-error">{dialogState.passwordError}</span>}
              </div>
            )}

            <div className="site-dialog-actions">
              <button className="btn-site-dialog cancel" onClick={() => closeSiteDialog(false)}>{dialogState.cancelLabel}</button>
              <button className="btn-site-dialog confirm" onClick={confirmSiteDialog}>{dialogState.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FloorPlan;
